// ContainerPage — a course or module page (pen "Always writing", Decided
// 30 Oct: one editing mode; placed by pen Q76): the title, the facts line
// (what it is, how many parts it holds, the project's sources), then its
// body, a document like every other page:
// the author's own text above, between and below its parts, each part an
// object among the text (PartLine, a tile), and the writing line at the
// end ("Write, or type / for more"). The body is the host's editor
// (`body`, given a way to draw a part by id); while it is not ready, the
// parts show as a list (Lines). An older introduction moved to its
// Introduction page stays there (#344); new text lives on the page itself.
//
// The project's own page with no single course has no text to write on: it
// lists the project's courses, modules and files; empty, it shows the first
// line, which opens the Create menu (`add`, from the host). A line that asks
// (`ask`) stands where the part it asks for would land.
//
// Pure: what is shown is projected in; every change goes out as an intent.

export { ContainerPage };

import type { ReactNode } from "react";
import type { ContainerCard } from "../Studio/studioCards.js";
import { NoteLine } from "../StudioDesk/DeskBriefs.js";
import type { DeskNote } from "../StudioDesk/deskFacts.js";
import {
    AskRow,
    type LineAsk,
    type LineIntents,
    Lines,
    PartLine,
} from "./Lines.js";
import { PageFacts, pageFactsOf } from "./PageFacts.js";
import { PageTitle } from "./PageTitle.js";
import type { LandingChoice } from "./studioCreate.js";
import type { PageLink } from "./studioPages.js";

const ContainerPage = ({
    title,
    container,
    links,
    online,
    note,
    sources,
    onSources,
    add,
    places,
    onRename,
    intents,
    ask,
    body,
}: {
    // A line that asks, where the part would land (Ask here from a button).
    readonly ask?: LineAsk;
    readonly title: string;
    // The course or module it is (absent: the project as a whole).
    readonly container: ContainerCard | undefined;
    readonly links: readonly PageLink[];
    readonly online: boolean;
    readonly note: DeskNote | undefined;
    // The project's sources (undefined: not known yet).
    readonly sources: number | undefined;
    readonly onSources: () => void;
    // The first line of an empty page with no text of its own, and its
    // Create menu.
    readonly add: ReactNode;
    // Every container a part can move to.
    readonly places: readonly LandingChoice[];
    // A new name for it (absent: not renamed here, as the project's page).
    readonly onRename: ((title: string) => void) | undefined;
    readonly intents: LineIntents;
    // Its text with the parts among it, given how to draw a part by its id
    // (null: not ready, the parts show as a list; absent: nothing to write
    // on, the project's own page).
    readonly body?: (part: (id: string) => ReactNode) => ReactNode | null;
}): ReactNode => {
    // A part's tile among the text; the asking line before the part at its
    // place. A part with no line here (a source) shows nothing.
    const part = (id: string): ReactNode => {
        const link = links.find((l) => l.id === id);
        if (link === undefined) return null;
        const tile = (
            <PartLine
                link={link}
                online={online}
                places={places}
                intents={intents}
                standalone
            />
        );
        return ask !== undefined && ask.at === link.holder?.at ? (
            <>
                <AskRow line={ask.line} standalone />
                {tile}
            </>
        ) : (
            tile
        );
    };
    const written = body?.(part) ?? null;
    // The asking line past the last part stands after the text.
    const askAtEnd =
        ask !== undefined &&
        !links.some((l) => l.holder !== undefined && l.holder.at === ask.at);
    return (
        <article
            className="bilda-pages__page"
            aria-label={title}
            data-kind={container?.kind ?? "root"}
        >
            <PageTitle title={title} onRename={onRename} />
            <PageFacts
                facts={pageFactsOf({
                    kind: container?.kind ?? null,
                    suggestion: false,
                    aiAuthored: false,
                    parts: container === undefined ? undefined : links.length,
                    sources,
                })}
                onSources={onSources}
            />
            {written !== null ? (
                <div className="bilda-pages__page-middle">
                    {written}
                    {askAtEnd ? <AskRow line={ask.line} standalone /> : null}
                </div>
            ) : links.length === 0 && ask === undefined ? (
                add
            ) : (
                <Lines
                    links={links}
                    online={online}
                    places={places}
                    intents={intents}
                    ask={ask}
                />
            )}
            {note === undefined ? null : <NoteLine note={note} />}
        </article>
    );
};
